/**
 * Thin fetch wrapper for the EMS API.
 *
 * Contract:
 *   * Every call is authenticated. The bearer token is pulled just-in-time
 *     from the live Keycloak instance so we never cache a stale copy in
 *     React state.
 *   * The API always returns `{ error: { code, message } }` on failures
 *     (either at the top level for auth errors, or wrapped in `detail`
 *     for FastAPI-raised HTTPException). We normalize both into the same
 *     `ApiError`.
 *   * 401 from the server means the token Keycloak handed us was rejected
 *     (e.g. the signing key rotated faster than the client refreshed).
 *     The caller receives an `ApiError` with `code = "unauthenticated"`;
 *     the UI decides whether to retry or send the user back to login.
 */

import { config } from "@/config";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly payload: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

type TokenSource = () => string | undefined | Promise<string | undefined>;

export type FetchOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
};

export function createApiClient(getToken: TokenSource, fetchImpl: typeof fetch = fetch) {
  async function request<T>(path: string, options: FetchOptions = {}): Promise<T> {
    const token = await getToken();
    if (!token) {
      throw new ApiError(401, "unauthenticated", "no access token", null);
    }

    const url = buildUrl(path, options.query);
    const response = await fetchImpl(url, {
      method: options.method ?? "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json") ? await response.json() : await response.text();

    if (!response.ok) {
      const { code, message } = extractError(payload, response.status);
      throw new ApiError(response.status, code, message, payload);
    }
    return payload as T;
  }

  return { request };
}

export type ApiClient = ReturnType<typeof createApiClient>;

function buildUrl(path: string, query?: FetchOptions["query"]): string {
  const base = config.apiBaseUrl.replace(/\/+$/u, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${base}${cleanPath}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

function extractError(payload: unknown, status: number): { code: string; message: string } {
  // Unified 401/403 envelope: { error: { code, message } }
  if (isRecord(payload) && isRecord(payload.error)) {
    return {
      code: asString(payload.error.code) ?? `http_${status}`,
      message: asString(payload.error.message) ?? `request failed with ${status}`,
    };
  }
  // FastAPI HTTPException wrapper: { detail: { error: { code, message } } }
  if (isRecord(payload) && isRecord(payload.detail) && isRecord(payload.detail.error)) {
    return {
      code: asString(payload.detail.error.code) ?? `http_${status}`,
      message: asString(payload.detail.error.message) ?? `request failed with ${status}`,
    };
  }
  // FastAPI validation errors: { detail: [{ msg, loc, ... }] }
  if (isRecord(payload) && Array.isArray(payload.detail) && payload.detail.length > 0) {
    const first = payload.detail[0] as Record<string, unknown> | undefined;
    const msg = first ? asString(first.msg) : null;
    return { code: "validation_error", message: msg ?? "validation error" };
  }
  return { code: `http_${status}`, message: `request failed with ${status}` };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}
